import { db } from '../../../../Config/Config';

export const CLIENT_ID = 'maxpark-220731';
export const EXPENSES_COLLECTION = `clients/${CLIENT_ID}/expenses`;

const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const normalizeExpense = (value = {}, id = '') => ({
  id,
  vendor: String(value.vendor || '').trim(),
  category: String(value.category || '').trim(),
  product: String(value.product || '').trim(),
  date: String(value.date || '').slice(0, 10),
  amount: asNumber(value.amount),
  currency: value.currency === 'USD' ? 'USD' : 'CAD',
  // A foreign amount must never silently become a CAD value when its conversion
  // is absent. CAD expenses can safely use their original amount as the CAD value.
  amountCad: Number.isFinite(Number(value.amountCad))
    ? Number(value.amountCad)
    : value.currency === 'CAD'
      ? asNumber(value.amount)
      : 0,
  fxRateSource: value.fxRateSource === 'historical-calculated' ? 'historical-calculated' : 'statement',
  fxRate: asNumber(value.fxRate),
  fxRateDate: String(value.fxRateDate || '').slice(0, 10),
  taxes: asNumber(value.taxes),
  recurring: Boolean(value.recurring),
  cadence: String(value.cadence || ''),
  nextRenewal: String(value.nextRenewal || '').slice(0, 10),
  paymentMethod: String(value.paymentMethod || ''),
  receiptUrl: String(value.receiptUrl || '').trim(),
  note: String(value.note || '').trim(),
  entryType: value.entryType === 'credit-adjustment' ? 'credit-adjustment' : 'expense',
  reconciliationStatus: ['confirmed-portal', 'to-validate', 'unpaid'].includes(value.reconciliationStatus)
    ? value.reconciliationStatus
    : 'to-validate',
  source: String(value.source || '').trim(),
  createdAt: value.createdAt || null,
});

// Keep granular FOCUS usage rows in Firestore for auditability, but do not
// present them as accounting transactions. They are allocation telemetry, not
// settled invoices, and can otherwise make the report look like it contains
// thousands of one-cent expenses. Paid Vercel invoices are recorded separately.
export const isReportableExpense = (expense = {}) => (
  expense.source !== 'vercel-focus'
);

export const loadExpenses = async () => {
  const snapshot = await db.collection(EXPENSES_COLLECTION).get({ source: 'server' });
  return snapshot.docs
    .map((document) => normalizeExpense(document.data(), document.id))
    .sort((left, right) => String(right.date).localeCompare(String(left.date)));
};

export const createExpense = async (expense = {}) => {
  const normalized = normalizeExpense(expense);
  const payload = {
    ...normalized,
    createdAt: new Date().toISOString(),
  };
  delete payload.id;

  const reference = await db.collection(EXPENSES_COLLECTION).add(payload);
  return normalizeExpense(payload, reference.id);
};

export const updateExpenseReconciliation = async (id, reconciliationStatus) => {
  if (!id || !['confirmed-portal', 'to-validate', 'unpaid'].includes(reconciliationStatus)) {
    throw new Error('Invalid expense reconciliation update');
  }
  await db.collection(EXPENSES_COLLECTION).doc(id).update({ reconciliationStatus });
  return reconciliationStatus;
};

export const expenseSummary = (expenses = []) => expenses.reduce((summary, expense) => {
  summary.totalCad += asNumber(expense.amountCad);
  summary.recurringCad += expense.recurring ? asNumber(expense.amountCad) : 0;
  summary.uncategorized += expense.category ? 0 : 1;
  return summary;
}, { totalCad: 0, recurringCad: 0, uncategorized: 0 });

export const categoryBreakdown = (expenses = []) => Object.entries(
  expenses.reduce((totals, expense) => {
    const category = expense.category || 'À catégoriser';
    totals[category] = (totals[category] || 0) + asNumber(expense.amountCad);
    return totals;
  }, {})
).sort(([, left], [, right]) => right - left);
